import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { translate } from '../../i18n';
import useMyDocuments from '../../hooks/useMyDocuments';
import { ACCEPTED_MIME_TYPES, MAX_DOCUMENT_BYTES, uploadMyDocuments } from '../../servics/accountDocumentsApi';

const formatDate = (iso, language) => {
    try {
        return new Date(iso).toLocaleDateString(language === 'EN' ? 'en-GB' : 'fr-FR');
    } catch {
        return iso;
    }
};

/** Dépôt le plus récent par type (la liste du backend est triée du plus récent au plus ancien). */
const latestByType = (documents) =>
    documents.reduce((latest, document) => (latest[document.type] ? latest : { ...latest, [document.type]: document }), {});

/**
 * « Mes documents » : le compte dépose ici les pièces qui ne sont plus demandées à l'inscription
 * (entreprise : registre du commerce, attestation fiscale ; particulier : pièce d'identité).
 */
const MyDocuments = () => {
    const language = useSelector((state) => state.lang?.lang) || 'FR';
    const token = useSelector((state) => state.auth?.token) || localStorage.getItem('token');
    const text = translate('account_documents', language) || translate('account_documents', 'FR');
    const { loading, documents, error, setDocuments } = useMyDocuments(token);
    const [selected, setSelected] = useState({});
    const [problems, setProblems] = useState({});
    const [sending, setSending] = useState(false);
    const [feedback, setFeedback] = useState(null);

    if (!token) {
        return (
            <section className="my-documents container py-5">
                <h1>{text.page_title}</h1>
                <p>{text.login_required}</p>
                <Link className="button" to="/login">{text.login_action}</Link>
            </section>
        );
    }

    if (loading && !documents) {
        return <section className="my-documents container py-5"><p>{text.loading}</p></section>;
    }

    if (!documents) {
        const notFound = error?.response?.status === 404;
        return (
            <section className="my-documents container py-5">
                <h1>{text.page_title}</h1>
                <p className="text-danger">{notFound ? text.no_registration : text.load_error}</p>
            </section>
        );
    }

    const profile = documents.profile === 'COMPANY' ? text.company : text.individual;
    const latest = latestByType(documents.documents || []);
    const choose = (type, file) => {
        let problem = null;
        if (file && !ACCEPTED_MIME_TYPES.includes(file.type)) problem = text.errors.DOCUMENT_TYPE_NOT_ALLOWED;
        else if (file && file.size > MAX_DOCUMENT_BYTES) problem = text.errors.DOCUMENT_TOO_LARGE;
        setProblems((previous) => ({ ...previous, [type]: problem }));
        setSelected((previous) => {
            const next = { ...previous };
            if (file && !problem) next[type] = file;
            else delete next[type];
            return next;
        });
    };

    const send = async (event) => {
        event.preventDefault();
        if (Object.keys(selected).length === 0) return;
        setSending(true);
        setFeedback(null);
        try {
            const updated = await uploadMyDocuments(selected);
            setDocuments(updated);
            setSelected({});
            setFeedback({ type: 'success', message: text.upload_success });
            event.target.reset();
        } catch (e) {
            const code = e?.response?.data?.error;
            setFeedback({ type: 'error', message: (code && text.errors[code]) || text.errors.default });
        } finally {
            setSending(false);
        }
    };

    const row = (type, required) => {
        const current = latest[type];
        return (
            <li key={type} className="my-documents__item">
                <div className="my-documents__item-head">
                    <label htmlFor={`document-${type}`}>
                        <strong>{text.types[type]?.label || type}</strong>
                        {required ? <span className="my-documents__required"> *</span> : <small> {text.optional}</small>}
                    </label>
                    {current && (
                        <span className={`my-documents__badge my-documents__badge--${current.status.toLowerCase()}`}>
                            {text.document_status[current.status]}
                        </span>
                    )}
                </div>
                {text.types[type]?.hint && <p className="my-documents__hint">{text.types[type].hint}</p>}
                {current && (
                    <p className="my-documents__current">
                        {current.filename} — {formatDate(current.uploadedAt, language)}
                        {current.status === 'REJECTED' && current.rejectionReason && (
                            <span className="text-danger"> — {current.rejectionReason}</span>
                        )}
                    </p>
                )}
                <input
                    id={`document-${type}`}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => choose(type, e.target.files?.[0])}
                />
                {problems[type] && <p className="text-danger">{problems[type]}</p>}
            </li>
        );
    };

    const optional = (documents.accepted || []).filter((type) => !documents.required.includes(type));

    return (
        <section className="my-documents container py-5">
            <h1>{profile.page_title}</h1>
            <p>{profile.why}</p>
            <p className={`my-documents__status my-documents__status--${documents.status.toLowerCase()}`} role="status">
                {text.status[documents.status]}
            </p>
            {documents.submittedAtRegistration && <p className="my-documents__hint">{text.submitted_at_registration}</p>}

            <form onSubmit={send}>
                <ul className="my-documents__list">
                    {documents.required.map((type) => row(type, true))}
                    {optional.map((type) => row(type, false))}
                </ul>
                <p className="my-documents__hint">{text.formats}</p>
                <button type="submit" className="button" disabled={sending || Object.keys(selected).length === 0}>
                    {sending ? text.sending : text.send}
                </button>
                {feedback && (
                    <p className={feedback.type === 'success' ? 'text-success mt-3' : 'text-danger mt-3'} role="alert">
                        {feedback.message}
                    </p>
                )}
            </form>
            <p className="my-documents__privacy">{text.privacy}</p>
        </section>
    );
};

export default MyDocuments;
